import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Modal } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import Cookies from 'js-cookie';
import { Tag } from 'primereact/tag';
import wc12PaymentService, { sbwcHomPage } from '../../../services/payments/wc12-payment/WC12PaymentService.ts';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FaCheck } from 'react-icons/fa';
import { Toast } from 'primereact/toast';
import logoSbwc from '../../../assets/logo_sbwc.jpg';

declare global {
  interface Window {
    tokenExpirationInterval: NodeJS.Timeout | null;
  }
}
const MEDIA_URL = process.env.REACT_APP_STATIC_MEDIA_URL;

const WC12PaymentConfirmationComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [invoiceData, setinvoiceData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const toastRef = React.useRef<Toast>(null);

  useEffect(() => {
    setLoading(true);
    const fetchPaymentDetails = async () => {
      try {
        console.log('WC12 Confirmation: fetching payment details...');
        // Use the paymentToken from localStorage (same token used throughout the WC12 flow)
        let authToken = Cookies.get('authToken');
        if (!authToken) {
          // Fallback to localStorage token used during the payment flow
          authToken = localStorage.getItem('paymentToken') || undefined;
        }
        console.log('WC12 Confirmation: authToken:', authToken ? 'found' : 'undefined');
        
        if (!authToken) {
          throw new Error('No token found');
        }

        const paymentDetails = await wc12PaymentService.getPaymentDetailsWithToken(authToken);
        console.log('WC12 Confirmation: paymentDetails response:', paymentDetails);

        const data: any = {
          status: paymentDetails.paymentStatus,
          invoiceNumber: paymentDetails.invoiceId,
          invoiceDate: paymentDetails.invoiceDate,
          claimNumber: paymentDetails.claimNo,
          claimantName: paymentDetails.claimantName,
          paymentDate: paymentDetails.paymentDate,
          invoiceAmount: paymentDetails.invoiceAmount,
          email: paymentDetails.email,
          acknowledgementNumber: paymentDetails.acknowledgementNumber,
          paymentStatus: paymentDetails.paymentStatus,
          amountPaid: paymentDetails.amountPaid
        };

        setinvoiceData([data]);
        setLoading(false);
      } catch (error) {
        console.error('WC12 Confirmation: error fetching payment details:', error);
        const status = error?.response?.status;
        // Clear any large auth cookies to prevent 431 errors
        Cookies.remove('authToken');
        Cookies.remove('authToken', { path: '/' });
        if (status == 401) {
          navigate('/paymenttokenexpired');
        } else {
          toastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'An error occurred. Please try again later.',
            life: 3000,
          });
        }
        setLoading(false);
      }
    };
    fetchPaymentDetails();
  }, []);

  const formatNumberWithCommas = (value: string | number): string => {
    if (typeof value === 'string') {
      value = value.replace(/[^0-9.]/g, '');
    }
    const num = Number(value);
    if (isNaN(num)) return '$0';
    return `${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const handleToSBWCPage = () => {
    window.location.href = `${sbwcHomPage}`;
  };

  const handleMakeOtherPayment = () => {
    navigate('/wc12payments');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <Toast ref={toastRef} />
      <Row className="justify-content-center ">
        <Col xs={12} md={8} className="text-center">
          <Card className="col-md-12 card-section mt-4 mx-auto" style={{ maxWidth: '100%', position: 'relative' }}>
            <div className="justify-content-center " style={{ backgroundColor: '#b31942', padding: '10px' }}>
              <Col xs="auto" className="text-start">
                <h5 className="text-white">Payment Confirmation</h5>
              </Col>
            </div>
            <h5 className="text-start text-black">Payment Information</h5>

            <DataTable value={invoiceData} responsiveLayout="stack" className='custom-table'>
              <Column field="invoiceNumber" header="Invoice Number" body={(rowData) => rowData.invoiceNumber} />
              <Column field="invoiceDate" header="Invoice Date" body={(rowData) => rowData.invoiceDate} className='nowrap-cell' />
              <Column field="claimNumber" header="Claim Number" body={(rowData) => rowData.claimNumber} />
              <Column field="claimantName" header="Claimant Name" body={(rowData) => rowData.claimantName} />
            </DataTable>
            <div style={{ marginTop: '2rem' }}></div>

            <DataTable value={invoiceData} responsiveLayout="stack" className='custom-table'>
              <Column field="invoiceAmount" header="Invoice Amount" body={(rowData) => formatNumberWithCommas(`${rowData.invoiceAmount}`)} />
              <Column
                header="Amount Paid"
                body={(rowData) => formatNumberWithCommas(`${rowData.amountPaid}`)}
              />
              <Column field="paymentStatus" header="Payment Status" body={(rowData) => rowData.paymentStatus} />
              {invoiceData.some(row => row.paymentDate) && (
                <Column field="paymentDate" header="Payment Date" body={(rowData) => rowData.paymentDate} className='nowrap-cell' />
              )}
            </DataTable>

            {invoiceData?.length > 0 && (
              <div
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                className="mt-2"
              >
                {invoiceData[0]?.status === 'Success' ? (
                  <>
                    <Tag
                      severity="success"
                      value={
                        invoiceData[0]?.acknowledgementNumber
                          ? `Payment processed successfully! \n Acknowledgement Number: ${invoiceData[0].acknowledgementNumber}`
                          : 'Payment submitted successfully'
                      }
                      className="tag-info-display"
                    />
                    <p style={{ marginTop: '8px', textAlign: 'center' }}>
                      A copy of this receipt is sent to email: {invoiceData[0]?.email}
                    </p>
                  </>
                ) : (
                  <Tag severity="warning" value="Payment cancelled!" className="tag-info-display" />
                )}
              </div>
            )}

            <Row className="justify-content-center mt-3">
              <Col xs="auto" className="mb-2">
                <Button
                  className="p-button-info"
                  style={{ backgroundColor: '#0A3161', height: '100%' }}
                  onClick={handleMakeOtherPayment}
                  icon={<FaCheck />}>
                  Make Another Payment
                </Button>
              </Col>
              <Col xs="auto" className="mb-2"></Col>
              <Col xs="auto" className="mb-2">
                <Button
                  className="p-button-info"
                  style={{ backgroundColor: '#0A3161' }}
                  onClick={handleToSBWCPage}>
                  <img src={logoSbwc} alt="Go to SBWC" className="logo img-fluid" style={{ width: '30px', height: '30px', marginRight: '8px' }} />
                  Go to SBWC
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WC12PaymentConfirmationComponent;
